import { useEffect, useState } from 'react';
import type { AuditAction } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Loader2 } from 'lucide-react';

interface CloseActionDialogProps {
  action: AuditAction | null;
  /** completed or cancelled */
  status: 'completed' | 'cancelled';
  onOpenChange: (open: boolean) => void;
  /** true on success, else the message shown in the dialog. */
  onClose: (id: number, status: 'completed' | 'cancelled', notes: string) => Promise<true | string>;
}

/** Close an action as completed or cancelled — completion notes are required. */
export default function CloseActionDialog({ action, status, onOpenChange, onClose }: CloseActionDialogProps) {
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (action) { setNotes(''); setError(null); } }, [action]);

  const submit = async () => {
    if (!action || !notes.trim()) return;
    setSaving(true);
    setError(null);
    const result = await onClose(action.id, status, notes.trim());
    setSaving(false);
    if (result === true) onOpenChange(false);
    else setError(result);
  };

  return (
    <Dialog open={action !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{status === 'completed' ? 'Close action as done' : 'Cancel action'}</DialogTitle>
          <DialogDescription className="line-clamp-2">{action?.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="close-action-notes">Completion notes *</Label>
          <Textarea id="close-action-notes" rows={3} className="text-base" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder={status === 'completed' ? 'What was done, and how it was checked' : 'Why it is no longer needed'} />
        </div>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button size="lg" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Keep open</Button>
          <Button size="lg" onClick={submit} disabled={!notes.trim() || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Close action
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
